import React, { useState, useEffect } from 'react';

import { Row, Form, InputGroup } from 'react-bootstrap';
import { MdWarning } from 'react-icons/md';
import Select from 'react-select';

import { FileUploadButton } from '../../../../components';
import { validateExpenseDate, getDateInputConstraints, updateReceiptError } from '../../../../utils/dateValidation';
import { DisableClickOverlay, isBilledOrTransmitted } from '../../../../utils/utilities';
import DocumentRow from '../documentRow';
import { uploadExpenseFile } from '../operations';

import './styles.scoped.css';

const Receipt = ({
  id,
  receipt,
  receipts,
  handleDeleteRow,
  debouncedSetReceiptField,
  receiptsErrors,
  setReceiptFile,
  setUploading,
  setUploadError,
  deleteDocumentFromReceipt,
  form,
  isCR,
  expenseId,
  paymentOptions,
  setReceipts,
  setReceiptField,
  setReceiptsErrors,
}) => {
  const [receiptAmountValue, setReceiptAmountValue] = useState(0);
  const [markupValue, setMarkupValue] = useState(0);

  useEffect(() => {
    const receiptAmountToUse = receiptAmountValue || 0;
    const markupToUse = markupValue || 0;
    setReceiptField(id, 'expenseTotal', receiptAmountToUse * (1 + parseFloat(markupToUse) / 100), receipts);
  }, [receiptAmountValue, markupValue]);

  useEffect(() => {
    setReceiptAmountValue(receipt.receiptAmount);
  }, [receipt.receiptAmount]);

  const customStyles = width => ({
    control: provided => ({
      ...provided,
      width: width,
      height: '30px',
    }),
    menuList: provided => ({
      ...provided,
      maxHeight: '200px',
    }),
    menu: provided => ({
      ...provided,
      zIndex: 100000,
    }),
  });

  useEffect(() => {
    setReceiptAmountValue(receipt.receiptAmount || 0);
  }, [receipt.receiptAmount]);

  useEffect(() => {
    setMarkupValue(receipt.markup || 0);
  }, [receipt.markup]);

  return (
    <div className="receipt-wrapper">
      {!receipt.documentName && (
        <div className="warning-container">
          <MdWarning size={36} title="Receipt document was not attached" color="#da0f2e" />
        </div>
      )}
      {receipts.length > 1 && (
        <div onClick={() => handleDeleteRow(id)} className="delete-row">
          Delete Row
        </div>
      )}
      <DisableClickOverlay isDisabled={isBilledOrTransmitted(form) || isCR || form?.status === 'Voided'}>
        <Row className="top-row" style={{ margin: '5px 0 0 0' }}>
          <div className="top-col" style={{ width: '40%', justifyContent: 'flex-start' }}>
            <div className="below-container">
              <Form.Group controlId="otherExpenseName">
                <Form.Label style={{ marginTop: '15px' }}>
                  <div className="required">Expense Name</div>
                </Form.Label>
                <Form.Control
                  key={`${id}otherExpenseName`}
                  style={{
                    width: '300px',
                    height: '30px',
                    paddingLeft: 10,
                  }}
                  type="input"
                  name="otherExpenseName"
                  defaultValue={receipts[id]?.otherExpenseName}
                  onChange={e => {
                    const updated = [...receipts];
                    updated[id].otherExpenseName = e.target.value;
                    setReceipts(updated);
                  }}
                  isInvalid={!!receiptsErrors[id]?.otherExpenseName}
                />
                <Form.Control.Feedback type="invalid">{receiptsErrors[id]?.otherExpenseName}</Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>
          <div className="top-col">
            <Form.Group controlId="otherExpenseDescription">
              <Form.Label style={{ marginTop: '15px' }}>
                <div className="required">Expense Description</div>
              </Form.Label>
              <Form.Control
                key={`${id}otherExpenseDescription`}
                style={{
                  width: '300px',
                  height: '30px',
                  paddingLeft: 10,
                }}
                type="input"
                name="otherExpenseDescription"
                defaultValue={receipt?.otherExpenseDescription}
                onChange={event => {
                  debouncedSetReceiptField({
                    id: id,
                    field: 'otherExpenseDescription',
                    value: event.target.value,
                    receipts: receipts,
                  });
                }}
                isInvalid={!!receiptsErrors[id]?.otherExpenseDescription}
              />
              <Form.Control.Feedback type="invalid">
                {receiptsErrors[id]?.otherExpenseDescription}
              </Form.Control.Feedback>
            </Form.Group>
          </div>
          <div className="top-col">
            <Form.Group controlId="otherExpenseDate">
              <Form.Label style={{ marginTop: '15px' }}>
                <div className="required">Expense Date</div>
              </Form.Label>
              <Form.Control
                style={{ width: '165px', height: '30px' }}
                name="otherExpenseDate"
                type="date"
                as="input"
                defaultValue={receipt?.otherExpenseDate}
                min={getDateInputConstraints().min}
                max={getDateInputConstraints().max}
                onChange={event => {
                  const validation = validateExpenseDate(event.target.value, 'Expense Date');
                  updateReceiptError(
                    id,
                    'otherExpenseDate',
                    validation.isValid ? null : validation.error,
                    receiptsErrors,
                    setReceiptsErrors,
                  );
                  debouncedSetReceiptField({
                    id: id,
                    field: 'otherExpenseDate',
                    value: event.target.value,
                    receipts: receipts,
                  });
                }}
                isInvalid={!!receiptsErrors[id]?.otherExpenseDate}
              />
              <Form.Control.Feedback type="invalid">{receiptsErrors[id]?.otherExpenseDate}</Form.Control.Feedback>
            </Form.Group>
          </div>
        </Row>
      </DisableClickOverlay>
      <DisableClickOverlay>
        {form?.lob === 'DMS' && (
          <>
            <Row className="top-row" style={{ margin: '5px 0 0 0' }}>
              <div className="top-col" style={{ width: '40%', justifyContent: 'flex-start' }} />
              <div className="top-col" style={{ justifyContent: 'flex-start' }}>
                <div className="below-container" style={{ justifyContent: 'flex-start', width: '300px' }}>
                  <Form.Group controlId="paidThrough">
                    <Form.Label style={{ marginTop: '15px' }}>
                      <div className="required">Paid Through</div>
                    </Form.Label>
                    <Select
                      styles={customStyles('300px')}
                      options={paymentOptions}
                      name="paidThrough"
                      onChange={(event, action) => {
                        if (action.action === 'clear') {
                          const updated = [...receipts];
                          updated[id].paidThrough = '';
                          updated[id].creditCardHolderName = '';
                          setReceipts(updated);
                        }
                        if (action.action === 'select-option') {
                          const updated = [...receipts];
                          updated[id].paidThrough = event.value;
                          updated[id].creditCardHolderName = '';
                          setReceipts(updated);
                        }
                      }}
                      onBlur={() => {
                        if (receipt.paidThrough !== 'Credit Card') {
                          receipts[id] = {
                            ...receipts[id],
                            creditCardHolderName: '',
                          };
                        }
                      }}
                      value={paymentOptions?.find(option => option.value === receipt?.paidThrough)}
                      isInvalid={!!receiptsErrors[id]?.paidThrough}
                    />
                    {receiptsErrors[id]?.paidThrough && <div className="error">{receiptsErrors[id]?.paidThrough}</div>}
                  </Form.Group>
                </div>
              </div>
              <div className="top-col">
                <Form.Group controlId="creditCardHolderName">
                  <InputGroup
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '150px',
                    }}>
                    <Form.Group controlId="creditCardHolderName">
                      <Form.Label
                        style={{
                          marginTop: '15px',
                        }}>
                        <div className="required">Credit Card Holder Name</div>
                      </Form.Label>
                      <Form.Control
                        style={{
                          width: '300px',
                          height: '30px',
                          paddingLeft: 10,
                        }}
                        disabled={receipt?.paidThrough !== 'Credit Card'}
                        type="input"
                        name="creditCardHolderName"
                        value={receipt?.paidThrough === 'Credit Card' ? receipt?.creditCardHolderName : ''}
                        defaultValue={receipt?.creditCardHolderName}
                        onChange={e => {
                          const updated = [...receipts];
                          updated[id].creditCardHolderName = e.target.value;
                          setReceipts(updated);
                        }}
                        isInvalid={!!receiptsErrors[id]?.creditCardHolderName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {receiptsErrors[id]?.creditCardHolderName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </InputGroup>
                </Form.Group>
              </div>
            </Row>
          </>
        )}
      </DisableClickOverlay>
      <DisableClickOverlay isDisabled={isBilledOrTransmitted(form) || isCR || form?.status === 'Voided'}>
        <Row className="top-row" style={{ margin: '5px 0 0 0', alignItems: 'start' }}>
          <div className="top-col" style={{ width: '40%', justifyContent: 'flex-start' }}>
            <div className="below-container" style={{ justifyContent: 'flex-end' }}>
              <div
                className="below-container"
                style={{
                  justifyContent: 'space-between',
                  width: '300px',
                }}>
                <Form.Group controlId="receiptAmount">
                  <Form.Label style={{ marginTop: '15px' }}>
                    <div>Receipt Amount</div>
                  </Form.Label>
                  <InputGroup style={{ display: 'flex', alignItems: 'center' }}>
                    <span
                      style={{
                        position: 'absolute',
                        left: '10px',
                        zIndex: 10000,
                      }}>
                      $
                    </span>
                    <Form.Control
                      key={`${id}receiptAmount`}
                      style={{
                        width: '150px',
                        height: '30px',
                        padding: 0,
                        paddingLeft: '25px',
                      }}
                      type="number"
                      step="0.01"
                      min="0"
                      name="receiptAmount"
                      value={receiptAmountValue}
                      onChange={event => {
                        setReceiptAmountValue(event.target.value);
                        setReceiptField(id, 'receiptAmount', Number(event.target.value), receipts);
                      }}
                      isInvalid={!!receiptsErrors[id]?.receiptAmount}
                    />
                  </InputGroup>
                </Form.Group>
                <Form.Group controlId="markup">
                  <Form.Label style={{ marginTop: '15px' }}>
                    <div className="required">Markup</div>
                  </Form.Label>
                  <InputGroup style={{ display: 'flex', alignItems: 'center' }}>
                    <span
                      style={{
                        position: 'absolute',
                        right: '10px',
                        zIndex: 10000,
                      }}>
                      %
                    </span>
                    <Form.Control
                      style={{
                        width: '75px',
                        height: '30px',
                        padding: 0,
                        paddingLeft: '10px',
                      }}
                      type="number"
                      step="1"
                      min="0"
                      name="markup"
                      value={markupValue}
                      onChange={event => {
                        setMarkupValue(event.target.value);
                        setReceiptField(id, 'markup', event.target.value, receipts);
                      }}
                      isInvalid={!!receiptsErrors[id]?.markup}
                    />
                  </InputGroup>

                  <Form.Control.Feedback type="invalid">{receiptsErrors[id]?.markup}</Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>
          </div>
          <div className="top-col">
            <Form.Group controlId="expenseTotal">
              <Form.Label style={{ marginTop: '15px' }}>Expense Total</Form.Label>
              <InputGroup
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '150px',
                  opacity: '0.7',
                }}>
                <span
                  style={{
                    position: 'absolute',
                    left: '10px',
                    zIndex: 10000,
                  }}>
                  $
                </span>
                <Form.Control
                  style={{
                    width: '225px',
                    height: '30px',
                    padding: 0,
                    border: '0px',
                    backgroundColor: 'white',
                    marginLeft: '20px',
                  }}
                  as="input"
                  name="location"
                  readOnly
                  disabled
                  value={
                    receipt?.expenseTotal ? parseFloat(receipt?.expenseTotal).toFixed(2) : parseFloat(0).toFixed(2)
                  }
                />
              </InputGroup>
            </Form.Group>
          </div>
          {!receipt?.documentName && expenseId ? (
            <div className="top-col">
              <Form.Group controlId="receipt" style={{ display: 'flex', flexDirection: 'column' }}>
                <Form.Label style={{ marginTop: '15px' }}>
                  <div>Receipt</div>
                </Form.Label>

                <Form.Control
                  name="receipt"
                  as="span"
                  style={{
                    border: 'none',
                    outline: 'none',
                    paddingLeft: 0,
                    height: 'auto',
                  }}>
                  <FileUploadButton
                    func={uploadExpenseFile}
                    setReceiptFile={setReceiptFile}
                    receiptId={id}
                    uploadStart={() => setUploading(true)}
                    uploadEnd={() => setUploading(false)}
                    uploadError={() => {
                      setUploading(false);
                      setUploadError('Upload failed. Please ensure you have a network connection and try again.');
                    }}
                    expenseId={expenseId}
                  />
                </Form.Control>
              </Form.Group>
            </div>
          ) : null}
        </Row>
      </DisableClickOverlay>
      {receipt?.documentName && (
        <DocumentRow
          id={id}
          deleteDocumentFromReceipt={deleteDocumentFromReceipt}
          receipt={receipt}
          disabled={isBilledOrTransmitted(form) || form.status === 'Voided' || isCR}
        />
      )}
      <div className="gray-divider-light" />
    </div>
  );
};

export default Receipt;
